<template>
  <div>
    <div v-if="typeof createAction === 'function' && $can('create', 'jobs')" class="row mb-3">
      <div class="col">
        <b-button :block="true" class="create" size="lg" variant="success" @click.prevent="create">
          <font-awesome-icon :icon="['fal', 'plus-circle']" />
          Create a New Job
        </b-button>
      </div>
    </div>

    <b-card class="mb-0" no-body>
      <template #footer>
        <strong>Jobs</strong>
        {{ $store.state.jobs.data.length }} / {{ formatNumber(listQuery.count) }}
        <loader :loading="listQuery.loading" color="#37a9e1" size="16px"></loader>
      </template>

      <b-card-body class="card-body-sm">
        <b-input-group>
          <input
            v-model="listQuery.search"
            class="form-control"
            placeholder="Search jobs..."
            type="text"
            @keyup="search"
          />

          <div class="searching-spinner">
            <loader :loading="listQuery.loading" color="#37a9e1" size="16px"></loader>
          </div>

          <b-input-group-append>
            <button class="btn btn-primary" type="button" @click.prevent="toggleAdvancedSearch">
              <font-awesome-icon v-if="advancedSearch" :icon="['fal', 'chevron-up']" />
              <font-awesome-icon v-else :icon="['fal', 'chevron-down']" />
            </button>
          </b-input-group-append>

          <button class="btn btn-outline-danger ml-1" type="button" @click.prevent="reset">
            <font-awesome-icon :icon="['fal', 'undo']" />
          </button>
        </b-input-group>

        <div v-show="advancedSearch">
          <div class="mt-2">
            <select v-model="listQuery.restriction" class="form-control" @change="immediateSearch">
              <option :value="null">Select for Restricted Search</option>
              <option
                v-for="(restriction, index) in $store.state.jobs.advancedSearchFields"
                :key="index"
                :value="restriction"
              >
                {{ restriction }}
              </option>
            </select>
          </div>

          <div class="mt-2 row">
            <div class="col text-center">
              <a
                v-tooltip="{
                  content: listQuery.fullSearch
                    ? 'Only Search Recent Jobs'
                    : 'Search Across All Jobs',
                  classes: ['badge-theme'],
                }"
                class="cursor text-20"
                :class="listQuery.fullSearch ? 'opacity-100' : 'opacity-25'"
                @click.prevent="
                  listQuery.fullSearch = !listQuery.fullSearch;
                  immediateSearch();
                "
              >
                <font-awesome-icon
                  :icon="['fad', 'archive']"
                  :class="listQuery.fullSearch ? 'text-blue' : ''"
                />
              </a>
            </div>
            <div class="col text-center">
              <a
                v-tooltip="{
                  content: ascending ? 'Show in Descending Order' : 'Show in Ascending Order',
                  classes: ['badge-theme'],
                }"
                class="cursor text-20"
                :class="ascending ? 'opacity-100' : 'opacity-25'"
                @click.prevent="
                  ascending = !ascending;
                  immediateSearch();
                "
              >
                <font-awesome-icon
                  :icon="['fad', 'sort-down']"
                  :class="ascending ? 'text-blue' : ''"
                />
              </a>
            </div>
            <div class="col text-center">
              <a
                v-tooltip="{
                  content: listQuery.today ? 'Show All Jobs' : 'Only Show Jobs Created Today',
                  classes: ['badge-theme'],
                }"
                class="cursor text-20"
                :class="listQuery.today ? 'opacity-100' : 'opacity-25'"
                @click.prevent="
                  listQuery.today = !listQuery.today;
                  immediateSearch();
                "
              >
                <font-awesome-icon
                  :icon="['fad', 'calendar-day']"
                  :class="listQuery.today ? 'text-blue' : ''"
                />
              </a>
            </div>
            <div class="col text-center">
              <a
                v-tooltip="{
                  content: listQuery.recurring
                    ? 'Show All Jobs, Including Recurring'
                    : 'Only Show Recurring Jobs',
                  classes: ['badge-theme'],
                }"
                class="cursor text-20"
                :class="listQuery.recurring ? 'opacity-100' : 'opacity-25'"
                @click.prevent="
                  listQuery.recurring = !listQuery.recurring;
                  immediateSearch();
                "
              >
                <font-awesome-icon
                  :icon="['fad', 'repeat']"
                  :class="listQuery.recurring ? 'text-blue' : ''"
                />
              </a>
            </div>
            <div class="col text-center">
              <a
                v-tooltip="{
                  content: listQuery.rescheduling
                    ? 'Show All Jobs, Including Rescheduled'
                    : 'Only Show Rescheduled Jobs',
                  classes: ['badge-theme'],
                }"
                class="cursor text-20"
                :class="listQuery.rescheduling ? 'opacity-100' : 'opacity-25'"
                @click.prevent="
                  listQuery.rescheduling = !listQuery.rescheduling;
                  immediateSearch();
                "
              >
                <font-awesome-icon
                  :icon="['fad', 'history']"
                  :class="listQuery.rescheduling ? 'text-blue' : ''"
                />
              </a>
            </div>
            <div class="col text-center">
              <a
                v-tooltip="{
                  content: listQuery.withAcceptedQuotes
                    ? 'Show All Jobs, Including Accepted Quotes'
                    : 'Only Show Jobs with Accepted Quotes',
                  classes: ['badge-theme'],
                }"
                class="cursor text-20"
                :class="listQuery.withAcceptedQuotes ? 'opacity-100' : 'opacity-25'"
                @click.prevent="
                  listQuery.withAcceptedQuotes = !listQuery.withAcceptedQuotes;
                  immediateSearch();
                "
              >
                <font-awesome-icon
                  :icon="['fad', 'thumbs-up']"
                  :class="listQuery.withAcceptedQuotes ? 'text-blue' : ''"
                />
              </a>
            </div>
          </div>

          <div class="mt-2 text-right">
            <span class="cursor text-blue" @click="advancedDetailSearch = !advancedDetailSearch">
              <template v-if="advancedDetailSearch">Less Filters</template>
              <template v-else>More Filters</template>
            </span>
          </div>

          <div v-show="advancedDetailSearch">
            <div class="mt-2">
              <select v-model="listQuery.brandId" class="form-control" @change="immediateSearch">
                <option :value="null">Brand</option>
                <option v-for="brand in allBrandData" :key="brand.id" :value="brand.id">
                  {{ brand.name }}
                </option>
              </select>
            </div>

            <div v-if="$can('read', 'jobs', 'filter-by-technician')" class="mt-2">
              <multiselect
                v-model="technicianSelectObject"
                :custom-label="technicianSelectFullname"
                :internal-search="false"
                :limit="3"
                :limit-text="technicianSelectLimitText"
                :loading="technicianSelectIsLoading"
                :options="technicianSelectOptions"
                :options-limit="10"
                :searchable="true"
                :show-labels="false"
                label="email"
                placeholder="Technician"
                track-by="id"
                @input="immediateSearch"
                @search-change="technicianSelectAsyncFind"
              >
                <template #noResult>Oops! No result found.</template>
              </multiselect>
            </div>

            <div class="mt-2">
              <form-service-types-select
                v-model="listQuery.serviceType"
                :group-select="true"
                :labelless="true"
                :multiple="false"
                :show-labels="false"
                bottom-margin=""
                name="service_type"
                placeholder="Select Service Type"
                @change="updateServiceType"
              />
            </div>

            <div class="mt-2 row">
              <div class="col">
                <select v-model="listQuery.status" class="form-control" @change="immediateSearch">
                  <option :value="null">Status</option>
                  <option
                    v-for="(status, index) in $store.state.jobs.jobStatuses"
                    :key="index"
                    :value="status"
                  >
                    {{ status }}
                  </option>
                </select>
              </div>
              <div class="col">
                <select v-model="listQuery.followUp" class="form-control" @change="immediateSearch">
                  <option :value="null">Follow-up</option>
                  <option value="0">New Enquiry</option>
                  <option value="1">1st Contact</option>
                  <option value="2">2nd Contact</option>
                  <option value="3">3rd Contact</option>
                  <option value="4">More Contact</option>
                  <option value="6">Unreachable</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </b-card-body>

      <!-- Not using b-list-group because it seems to break v-infinite-scroll -->
      <ul
        ref="jobList"
        v-infinite-scroll="loadMore"
        :style="{ maxHeight: listMaxHeight }"
        class="list-group list-group-flush overflow-y-auto job-list"
        infinite-scroll-disabled="listQuery.loading"
        infinite-scroll-distance="10"
      >
        <li
          v-for="(job, index) in $store.state.jobs.data"
          v-show="job.status !== 'Draft'"
          :key="`${job.id}-${index}`"
          :class="[job.id === currentJobId ? 'active' : '']"
          class="list-group-item cursor"
          @click="goto(job.id)"
        >
          <div :class="job | jobStatusClass" class="wrapper">
            <div class="status status-background">
              {{ job.status | capitalLetter }}

              <div v-if="job.is_recurring">[R]</div>

              <follow-up-dice :job="job" />
            </div>

            <div class="content">
              <div class="job-list__brand-logo">
                <img
                  v-if="job && job.brand && job.brand.logo_small"
                  :alt="job.brand.name"
                  :src="job.brand.logo_small"
                />
              </div>

              <div class="job-list__id">#{{ job.id }}</div>

              <div
                :style="job.brand && job.brand.logo_small ? 'padding-top: 3px' : ''"
                class="job-list__job-contact"
              >
                {{ $lodash.get(job, "job_contacts[0].name", "N/A") }}
              </div>

              <div v-if="$lodash.get(job, 'service_types').length" class="multiselect__tags-wrap">
                <span
                  v-for="serviceType in $lodash.get(job, 'service_types')"
                  :key="serviceType.id"
                  class="multiselect__tag"
                >
                  <span>{{ serviceType.name }}</span>
                </span>
              </div>

              <div class="job-list__address">
                {{ $lodash.get(job, "job_address.city") }}
                {{ $lodash.get(job, "job_address.state") }}
              </div>

              <div
                v-if="job.status === 'Inquiry' && job.callback_start && job.callback_end"
                class="job-list__callback text-right"
                :class="formatCallbackWindowClass(job)"
              >
                <div
                  v-if="job.callback_start !== '00:00:00'"
                  class="badge badge-large badge-primary"
                >
                  {{ formatCallbackWindow(job) }}
                </div>
                <div v-else class="badge badge-large badge-primary">Anytime</div>
              </div>
            </div>
          </div>
        </li>
      </ul>

      <div v-if="false" class="search-dimmer">
        <loader color="#37a9e1" size="16px"></loader>
      </div>
    </b-card>
  </div>
</template>

<script type="text/babel">
import moment from "moment";
import debounce from "debounce";
import infiniteScroll from "vue-infinite-scroll";
import loader from "vue-spinner/src/ClipLoader.vue";
import EventBus from "../../imports/event-bus";
import format from "../../mixins/format";

export default {
  directives: { infiniteScroll },

  components: {
    loader,
  },

  mixins: [format],

  props: {
    currentJobId: {
      type: Number,
      default: 0,
    },

    createAction: {
      type: Function,
      required: false,
    },

    gotoAction: {
      type: Function,
      required: false,
    },

    navigateAfterReset: {
      type: Boolean,
      default: false,
    },

    footerSearch: {
      type: Boolean,
      default: true,
    },

    query: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      initialised: false,
      allBrandData: [],
      advancedSearch: false,
      advancedDetailSearch: false,
      ascending: false,

      technicianSelectObject: null,
      technicianSelectOptions: [],
      technicianSelectIsLoading: false,

      listQuery: {},
    };
  },

  computed: {
    listMaxHeight() {
      let height = window.innerHeight - 304;

      if (this.advancedSearch) {
        height -= 110;
      }

      if (this.advancedDetailSearch) {
        height -= 172;
      }

      if (!this.$can("create", "jobs")) {
        height += 60;
      }

      return `${height}px`;
    },
  },

  async mounted() {
    await this.$store.dispatch("service-types/initialise");

    if (this.footerSearch) {
      EventBus.$on("list-inquiries", () => {
        this.searchInquiries();
      });
      EventBus.$on("list-quoted", () => {
        this.searchQuoted();
      });
      EventBus.$on("list-invoiced", () => {
        this.searchInvoiced();
      });
      EventBus.$on("list-rescheduling", () => {
        this.searchRescheduling();
      });
      EventBus.$on("list-withAcceptedQuotes", () => {
        this.searchWithAcceptedQuotes();
      });
    }

    this.init();
    this.allBrandData = await this.$store.dispatch("brands/getData");

    this.listQuery.loading = true;

    this.$store.dispatch("jobs/loadMore", this.listQuery).then((response) => {
      this.listQuery.loading = false;
      this.listQuery.count = response.body.total;
    });
  },

  methods: {
    init() {
      this.initialised = false;
      this.advancedSearch = false;
      this.advancedDetailSearch = false;
      this.ascending = false;

      this.technicianSelectObject = null;
      this.technicianSelectOptions = [];
      this.technicianSelectIsLoading = false;

      this.listQuery = Object.assign(
        {
          loading: true,
          search: "",
          page: 1,
          count: 0,
          fullSearch: false,
          technicianId: null,
          brandId: null,
          serviceType: null,
          serviceTypeId: null,
          status: null,
          rescheduling: false,
          withAcceptedQuotes: false,
          recurring: false,
          today: false,
          order: "desc",
          followUp: null,
          restriction: null,
        },
        this.query
      );

      this.initialised = true;
    },

    reset() {
      this.init();
      this.advancedSearch = false;

      this.$store
        .dispatch("jobs/reset", {
          listQuery: this.listQuery,
          navigateAfterReset: this.navigateAfterReset,
        })
        .then((response) => {
          this.listQuery.loading = false;
          this.listQuery.count = response.body.total;
          this.listQuery.fullSearch = false;

          EventBus.$emit("joblist-reset");

          if (this.$refs && this.$refs.jobList) {
            this.$nextTick(() => {
              this.$refs.jobList.scrollTop = 0;
            });
          }
        });
    },

    create() {
      if (typeof this.createAction === "function") {
        this.createAction();
      }
    },

    goto(id) {
      if (typeof this.gotoAction === "function") {
        this.gotoAction(id);
      }
    },

    loadMore() {
      if (this.listQuery.page < Math.ceil(this.listQuery.count / 10)) {
        this.listQuery.loading = true;
        this.listQuery.page = this.listQuery.page + 1;

        this.$store.dispatch("jobs/loadMore", this.listQuery).then(() => {
          this.listQuery.loading = false;
        });
      }
    },

    /* eslint-disable func-names */
    search: debounce(function () {
      if (this.listQuery.search.length < 2 && this.listQuery.search.length !== 0) {
        return;
      }

      this.listQuery.loading = true;
      this.listQuery.page = 1;

      if (this.technicianSelectObject) {
        this.listQuery.technicianId = this.technicianSelectObject.id;
      } else {
        this.listQuery.technicianId = null;
      }

      this.$store.dispatch("jobs/loadList", this.listQuery).then((response) => {
        this.listQuery.loading = false;
        this.listQuery.count = response.body.total;
      });
    }, 500),

    updateServiceType(serviceType) {
      this.listQuery.serviceType = serviceType;
      this.listQuery.serviceTypeId = serviceType.id;
      this.immediateSearch();
    },

    immediateSearch() {
      if (!this.$lodash.has(this.listQuery, "search")) {
        return;
      }
      if (this.listQuery.search.length < 2 && this.listQuery.search.length !== 0) {
        return;
      }

      this.listQuery.order = this.ascending ? "asc" : "desc";

      this.listQuery.loading = true;
      this.listQuery.page = 1;

      if (this.technicianSelectObject) {
        this.listQuery.technicianId = this.technicianSelectObject.id;
      } else {
        this.listQuery.technicianId = null;
      }

      this.$store.dispatch("jobs/loadList", this.listQuery).then((response) => {
        this.listQuery.loading = false;
        this.listQuery.count = response.body.total;
      });
    },

    technicianSelectFullname(user) {
      if (user.name) {
        return user.name;
      }

      return "";
    },

    technicianSelectLimitText(count) {
      return `and ${count} other technicians`;
    },

    /* eslint-disable func-names */
    technicianSelectAsyncFind: debounce(function (query) {
      if (query.length < 2) {
        return;
      }

      this.technicianSelectIsLoading = true;

      this.$http.get(`/users?search=${query}`).then((response) => {
        this.technicianSelectOptions = response.body.data;
        this.technicianSelectIsLoading = false;
      });
    }, 500),

    searchInquiries() {
      this.init();

      this.listQuery.loading = true;

      this.advancedSearch = true;
      this.advancedDetailSearch = true;
      this.listQuery.status = "Inquiry";
      this.listQuery.followUp = 0;

      this.$store.dispatch("jobs/loadList", this.listQuery).then((response) => {
        this.listQuery.loading = false;
        this.listQuery.count = response.body.total;
      });
    },

    toggleAdvancedSearch() {
      this.advancedSearch = !this.advancedSearch;
      this.listQuery.restriction = null;
    },

    searchQuoted() {
      this.init();

      this.listQuery.loading = true;

      this.advancedSearch = true;
      this.advancedDetailSearch = true;
      this.listQuery.status = "Quoted";
      this.listQuery.followUp = 0;

      this.$store.dispatch("jobs/loadList", this.listQuery).then((response) => {
        this.listQuery.loading = false;
        this.listQuery.count = response.body.total;
      });
    },

    searchInvoiced() {
      this.init();

      this.listQuery.loading = true;

      this.advancedSearch = true;
      this.advancedDetailSearch = true;
      this.listQuery.status = "Invoiced";
      this.listQuery.followUp = 0;

      this.$store.dispatch("jobs/loadList", this.listQuery).then((response) => {
        this.listQuery.loading = false;
        this.listQuery.count = response.body.total;
      });
    },

    searchRescheduling() {
      this.init();

      this.listQuery.loading = true;

      this.advancedSearch = true;
      this.listQuery.rescheduling = true;

      this.$store.dispatch("jobs/loadList", this.listQuery).then((response) => {
        this.listQuery.loading = false;
        this.listQuery.count = response.body.total;
      });
    },

    searchWithAcceptedQuotes() {
      this.init();

      this.listQuery.loading = true;

      this.advancedSearch = true;
      this.listQuery.withAcceptedQuotes = true;

      this.$store.dispatch("jobs/loadList", this.listQuery).then((response) => {
        this.listQuery.loading = false;
        this.listQuery.count = response.body.total;
      });
    },

    formatCallbackWindow(job) {
      if (!job.callback_start) {
        return "";
      }

      if (job.callback_start === "00:00:00") {
        return "Anytime";
      }

      return (
        moment(`2000-01-01 ${job.callback_start}`).format("hA") +
        " - " +
        moment(`2000-01-01 ${job.callback_end}`).format("hA")
      );
    },

    formatCallbackWindowClass(job) {
      const window = this.formatCallbackWindow(job).toLowerCase();
      return `job-list__callback-${window.replace(/ /g, "-").replace("---", "-")}`;
    },
  },
};
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
.list-group-item {
  border-top: 0;
  margin-bottom: 0;
}

.list-group-item > div:not(:last-child) {
  margin-bottom: 12px;
}

.advanced-switch {
  line-height: 35px;
}

.searching-spinner {
  position: absolute;
  right: 92px;
  top: 3px;
  font-size: 18px;
  z-index: 9999;
}

.search-dimmer {
  position: absolute;
  z-index: 9999;
  width: 100%;
  height: 667px;
  top: 76px;
  left: 0;
  text-align: center;
  background: #fff;
  opacity: 0.5;
  transition: all 0.5s;
}

.job-list {
  margin-top: -4px;

  .list-group-item {
    width: 100%;
    padding: 0 10px;
    color: #868282;

    &:hover:not(.active) {
      background-color: #ececec;
      transition: background-color 200ms linear;
    }

    &.active {
      border-top: 0;
      border-bottom: 0;

      color: #fff;

      .job-list__address {
        color: #fff;
      }
    }
  }

  .wrapper {
    overflow: hidden;
    width: 100%;
    height: 100%;
  }

  .status {
    width: 40px;
    padding-bottom: 9999px;
    margin-bottom: -9999px;
    padding-top: 8px;

    float: left;
    text-align: center;
    font-size: 20px;
    color: #fff;
  }

  .content {
    width: calc(100% - 40px);
    float: left;
    padding: 8px 10px 8px 15px;
    position: relative;
  }

  &__brand-logo {
    position: absolute;

    img {
      width: 120px;
    }
  }

  &__id {
    font-size: 18px;
    text-align: right;
  }

  &__job-contact {
    font-weight: 600;
    font-size: 18px;
  }

  &__address {
    font-weight: 600;
    font-size: 15px;
    text-transform: uppercase;
    color: #aab0be;
    padding-top: 3px;
  }

  .multiselect__tag {
    margin-right: 4px;
    font-size: 12px;
    padding: 3px 12px;
  }
}

.multiselect__tags-wrap {
  padding-top: 3px;
}

.job-list__callback-8am-12pm .badge {
  color: #23282c;
  background-color: #ffc107;
}

.job-list__callback-12pm-5pm .badge {
  color: #23282c;
  background-color: #bdea74;
}

.job-list__callback-5pm-8pm .badge {
  color: #fff;
  background-color: #36a9e1;
}

.job-list__callback-anytime .badge {
  color: #fff;
  background-color: #73818f;
}

::v-deep .multiselect__tags {
  padding-left: 15px;
  padding-right: 15px;
}

::v-deep .multiselect__single {
  overflow-x: hidden;
}

::v-deep .multiselect__content-wrapper {
  overflow-x: hidden;
}

::v-deep .multiselect__option--group {
  font-weight: bold;
  color: #fff;
  background-color: #36a9e1;
}
</style>
